/**
 * The wrapper document an untrusted email body is rendered inside.
 *
 * Pure (no React, no DOM) so it can be asserted on directly.
 *
 * LIGHT ONLY, on purpose. Mail is authored for a light canvas: inline
 * `color:#000`, white table backgrounds, dark-on-light logos. If this document
 * declared `color-scheme: light dark` it would follow the VIEWER'S BROWSER
 * dark mode (not the app theme), flip `Canvas` to black, and leave the
 * email's inline black text black — unreadable black-on-black (reported
 * 2026-10-04 on a customer reply in next-email-client). Pin it light.
 */

/** Avoid `</script>` in HTML fragments breaking srcDoc parsing when echoed into a document. */
function escapeClosingScriptTagsForSrcDoc(html: string): string {
  return html.replace(/<\/script/gi, "<\\/script");
}

export function buildEmailIframeSrcDoc(fragmentHtml: string): string {
  const bodyHtml = escapeClosingScriptTagsForSrcDoc(fragmentHtml);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><base target="_blank"><style>
/* Emails are authored for a light canvas (inline black text, white tables). Following the BROWSER's dark mode flips Canvas to black while that inline text stays black = unreadable. Pin light. */
html { color-scheme: light; }
html,body{margin:0;padding:10px 12px;font-family:ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:14px;line-height:1.6;background:Canvas;color:CanvasText;word-wrap:break-word;overflow-wrap:break-word;}
img,video{max-width:100%;height:auto;}
a{color:LinkText;text-decoration:underline;word-break:break-word;}
pre,code{white-space:pre-wrap;word-break:break-word;}
table{max-width:100%;border-collapse:collapse;}
blockquote{margin:0 0 0 8px;padding-left:10px;border-left:3px solid color-mix(in srgb, CanvasText 25%, transparent);}
[data-quote-toggle]{display:inline-flex;align-items:center;gap:4px;margin:4px 0;padding:1px 8px;font:inherit;font-size:13px;line-height:1.4;font-weight:600;letter-spacing:0.06em;color:color-mix(in srgb, CanvasText 60%, transparent);background:color-mix(in srgb, CanvasText 8%, transparent);border:1px solid color-mix(in srgb, CanvasText 18%, transparent);border-radius:12px;cursor:pointer;}
[data-quote-toggle]:hover{background:color-mix(in srgb, CanvasText 14%, transparent);color:CanvasText;}
[data-quote-content][hidden]{display:none;}
</style></head><body>${bodyHtml}</body></html>`;
}